import * as React from "react"
import TextareaAutosize from "react-textarea-autosize"
import { cn } from "@/lib/utils"

const InputGroup = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "group relative flex flex-col gap-2 rounded-2xl border border-border bg-card p-3 shadow-sm transition-all",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
})
InputGroup.displayName = "InputGroup"

const InputGroupTextarea = React.forwardRef(({ className, placeholder, ...props }, ref) => {
  return (
    <TextareaAutosize
      ref={ref}
      data-slot="input-group-control"
      className={cn(
        "flex field-sizing-content w-full resize-none rounded-lg bg-transparent px-3 py-2.5 text-base transition-[color,box-shadow] outline-none placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      placeholder={placeholder}
      minRows={1}
      maxRows={6}
      {...props}
    />
  )
})
InputGroupTextarea.displayName = "InputGroupTextarea"

const InputGroupText = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <span
      ref={ref}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    >
      {children}
    </span>
  )
})
InputGroupText.displayName = "InputGroupText"

const InputGroupAddon = React.forwardRef(({ className, align = "block-start", children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center",
        align === "block-end" && "mt-auto",
        align === "block-start" && "mb-auto",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
})
InputGroupAddon.displayName = "InputGroupAddon"

const InputGroupButton = React.forwardRef(({ className, size = "default", variant = "default", children, ...props }, ref) => {
  const sizeClasses = {
    sm: "h-8 px-3 text-xs",
    default: "h-9 px-4 text-sm",
    lg: "h-10 px-8",
    "icon-xs": "h-6 w-6 p-0",
  }

  const variantClasses = {
    default: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
    destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
    outline: "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
    secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
    ghost: "hover:bg-accent hover:text-accent-foreground",
  }

  return (
    <button
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
})
InputGroupButton.displayName = "InputGroupButton"

export { InputGroup, InputGroupAddon, InputGroupButton, InputGroupTextarea, InputGroupText }

